import {
  createContext,
  useContext,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { isDesktop } from "@shared/lib/desktop";
import type { ConnectionState } from "@shared/realtime";
import { Titlebar, type ChromeInbox, type ChromePlace } from "./Titlebar";

interface ChromeDetails {
  subtitle: string;
  connection?: ConnectionState;
  inbox?: ChromeInbox | null;
  place?: ChromePlace | null;
  /** Рабочий экран сохраняет привычную полосу и в браузере; входу она не нужна. */
  browserVisible: boolean;
}

interface ChromeRegistration {
  token: symbol;
  details: ChromeDetails;
}

type RegisterChrome = (registration: ChromeRegistration) => () => void;

const ChromeContext = createContext<RegisterChrome | null>(null);

export function AppChrome({
  notice,
  fallbackSubtitle = "Airel",
  children,
}: {
  notice?: ReactNode;
  fallbackSubtitle?: string;
  children: ReactNode;
}) {
  const [registration, setRegistration] = useState<ChromeRegistration | null>(null);
  const register = useCallback<RegisterChrome>((next) => {
    setRegistration(next);
    return () => {
      setRegistration((current) => (current?.token === next.token ? null : current));
    };
  }, []);
  const details = registration?.details;
  const showTitlebar = isDesktop() || details?.browserVisible;

  return (
    <ChromeContext.Provider value={register}>
      <div className="app-chrome">
        {showTitlebar && (
          <Titlebar
            subtitle={details?.subtitle ?? fallbackSubtitle}
            connection={details?.connection}
            inbox={details?.inbox ?? null}
            place={details?.place ?? null}
          />
        )}
        {notice}
        <div className="app-chrome-body">{children}</div>
      </div>
    </ChromeContext.Provider>
  );
}

/** Корневой экран сообщает общей рамке, что показать в единственной titlebar. */
export function useAppTitlebar(details: ChromeDetails, enabled = true): void {
  const register = useContext(ChromeContext);
  const token = useMemo(() => Symbol("titlebar"), []);
  const { subtitle, connection, browserVisible, inbox, place } = details;
  const inboxCount = inbox?.count ?? null;
  const inboxOpen = inbox?.open ?? null;
  // Место — простыми значениями: объект каждую отрисовку новый, и регистрация шла бы по кругу.
  const placeKind = place?.kind ?? null;
  const placeId = place?.kind === "server" ? place.id : null;
  const placeName = place?.kind === "server" ? place.name : null;
  const placeIcon = place?.kind === "server" ? place.iconId : null;

  useLayoutEffect(() => {
    if (!register || !enabled) return;
    const shown = inboxOpen && inboxCount !== null ? { count: inboxCount, open: inboxOpen } : null;
    const where: ChromePlace | null =
      placeKind === "server" && placeId && placeName !== null
        ? { kind: "server", id: placeId, name: placeName, iconId: placeIcon }
        : placeKind === "directs"
          ? { kind: "directs" }
          : null;
    return register({ token, details: { subtitle, connection, browserVisible, inbox: shown, place: where } });
  }, [
    register,
    token,
    enabled,
    subtitle,
    connection,
    browserVisible,
    inboxCount,
    inboxOpen,
    placeKind,
    placeId,
    placeName,
    placeIcon,
  ]);
}
